import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2, Command, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import * as authApi from '@/services/auth-api'

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState(false)
  
  const navigate = useNavigate()

  useEffect(() => {
    // Parse the URL hash to get the access token from Supabase redirect
    // Supabase redirects with: #access_token=...&refresh_token=...&type=recovery&...
    const hash = window.location.hash.substring(1)
    const params = new URLSearchParams(hash)
    
    const token = params.get('access_token')
    const refresh = params.get('refresh_token')
    const type = params.get('type')
    const errorCode = params.get('error')
    const errorDescription = params.get('error_description')
    
    if (errorCode) {
      setError(errorDescription || 'Invalid or expired reset link')
      setTokenError(true)
      return
    }
    
    if (token && refresh && type === 'recovery') {
      setAccessToken(token)
      setRefreshToken(refresh)
    } else if (!token || !refresh) {
      // No token in URL - user may have navigated here directly
      setTokenError(true)
      setError('No reset token found. Please request a new password reset link.')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (!accessToken || !refreshToken) {
      setError('No reset token found. Please request a new password reset link.')
      return
    }

    setIsLoading(true)

    try {
      await authApi.resetPassword(accessToken, refreshToken, password)
      setSuccess(true)
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } }
      const message =
        axiosError?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to reset password')
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className={cn("flex flex-col gap-6")}>
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <div className="p-6 md:p-8">
                {success ? (
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-green-500 text-white mb-2">
                        <CheckCircle className="size-6" />
                      </div>
                      <h1 className="text-2xl font-bold">Password updated!</h1>
                      <p className="text-muted-foreground text-balance">
                        Your password has been successfully reset. You will be redirected to the login page shortly.
                      </p>
                    </div>

                    <Field>
                      <Button asChild className="w-full">
                        <Link to="/login">
                          Continue to login
                        </Link>
                      </Button>
                    </Field>
                  </FieldGroup>
                ) : tokenError ? (
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-destructive text-destructive-foreground mb-2">
                        <AlertCircle className="size-6" />
                      </div>
                      <h1 className="text-2xl font-bold">Invalid reset link</h1>
                      <p className="text-muted-foreground text-balance">
                        This password reset link is invalid or has expired.
                      </p>
                    </div>

                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <Field>
                      <Button asChild className="w-full">
                        <Link to="/forgot-password">
                          Request new reset link
                        </Link>
                      </Button>
                    </Field>

                    <Field>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/login">
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Back to login
                        </Link>
                      </Button>
                    </Field>
                  </FieldGroup>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <FieldGroup>
                      <div className="flex flex-col items-center gap-2 text-center">
                        <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-2">
                          <Command className="size-6" />
                        </div>
                        <h1 className="text-2xl font-bold">Reset your password</h1>
                        <p className="text-muted-foreground text-balance">
                          Enter your new password below.
                        </p>
                      </div>

                      {error && (
                        <Alert variant="destructive">
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <Field>
                        <FieldLabel htmlFor="password">New password</FieldLabel>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                          autoFocus
                        />
                        <FieldDescription>
                          Must be at least 6 characters
                        </FieldDescription>
                      </Field>

                      <Field>
                        <FieldLabel htmlFor="confirmPassword">Confirm password</FieldLabel>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={6}
                        />
                      </Field>

                      <Field>
                        <Button type="submit" className="w-full" disabled={isLoading}>
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Updating password...
                            </>
                          ) : (
                            'Update password'
                          )}
                        </Button>
                      </Field>
                    </FieldGroup>
                  </form>
                )}
              </div>

              <div className="bg-muted relative hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80"
                  alt="Business meeting"
                  className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
